import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CalendarPlus,
  CheckCircle2,
  LogIn,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  UserCog,
  UsersRound,
} from 'lucide-react';
import {
  AdminUsersApiError,
  createAdminUser,
  deleteAdminUser,
  fetchAdminUsers,
  type AdminUserRecord,
  type AdminUsersDirectory,
} from '@/api/adminAdmins';
import { AdminAccountCard } from '@/components/admin/AdminAccountCard';
import {
  AdminCreateDialog,
  type AdminCreateFormValues,
} from '@/components/admin/AdminCreateDialog';
import { AdminDeleteDialog } from '@/components/admin/AdminDeleteDialog';
import { PageHeader } from '@/components/layouts/PageHeader';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { StatCard } from '@/components/ui/StatCard';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';

type StatusFilter = 'all' | 'active' | 'inactive';

const emptyForm: AdminCreateFormValues = {
  username: '',
  email: '',
  mobile: '',
  password: '',
  confirmPassword: '',
};

const emptyDirectory: AdminUsersDirectory = {
  admins: [],
  summary: {
    total: 0,
    active: 0,
    inactive: 0,
    createdThisMonth: 0,
    neverLoggedIn: 0,
  },
  currentAdminId: '',
  generatedAt: null,
};

function validateForm(values: AdminCreateFormValues): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!/^[\p{L}\p{N}._-]{3,80}$/u.test(values.username.trim())) {
    errors.username = 'Χρησιμοποιήστε 3–80 γράμματα, αριθμούς, τελεία, παύλα ή κάτω παύλα.';
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
    errors.email = 'Εισάγετε έγκυρο email.';
  }
  if (!/^[0-9+() .-]{10,30}$/.test(values.mobile.trim())) {
    errors.mobile = 'Εισάγετε έγκυρο τηλέφωνο 10–30 χαρακτήρων.';
  }
  if (values.password.length < 12 || values.password.length > 128) {
    errors.password = 'Ο κωδικός πρέπει να έχει 12–128 χαρακτήρες.';
  }
  if (values.password !== values.confirmPassword) {
    errors.confirmPassword = 'Οι δύο κωδικοί δεν ταιριάζουν.';
  }
  return errors;
}

export default function Admins() {
  const { toast } = useToast();
  const [directory, setDirectory] = useState<AdminUsersDirectory>(emptyDirectory);
  const [isFetching, setIsFetching] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [formValues, setFormValues] = useState<AdminCreateFormValues>(emptyForm);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AdminUserRecord | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadAdmins = useCallback(async (refresh = false) => {
    if (refresh) setIsRefreshing(true);
    else setIsFetching(true);
    setLoadError(null);

    try {
      setDirectory(await fetchAdminUsers());
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Δεν ήταν δυνατή η φόρτωση των διαχειριστών.';
      setLoadError(message);
      toast({ variant: 'destructive', title: 'Αποτυχία φόρτωσης', description: message });
    } finally {
      setIsFetching(false);
      setIsRefreshing(false);
    }
  }, [toast]);

  useEffect(() => {
    void loadAdmins();
  }, [loadAdmins]);

  const handleFormChange = (values: AdminCreateFormValues) => {
    const changedField = (Object.keys(values) as Array<keyof AdminCreateFormValues>)
      .find((field) => values[field] !== formValues[field]);
    setFormValues(values);
    setFormError(null);
    if (changedField) {
      setFieldErrors((current) => {
        const next = { ...current };
        delete next[changedField];
        if (changedField === 'password' || changedField === 'confirmPassword') {
          delete next.password;
          delete next.confirmPassword;
        }
        return next;
      });
    }
  };

  const handleCreateOpenChange = (open: boolean) => {
    if (isSubmitting) return;
    setCreateOpen(open);
    if (!open) {
      setFieldErrors({});
      setFormError(null);
    }
  };

  const handleCreate = async () => {
    const errors = validateForm(formValues);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setFormError('Ελέγξτε τα πεδία που έχουν επισημανθεί.');
      return;
    }

    setIsSubmitting(true);
    setFieldErrors({});
    setFormError(null);
    try {
      const nextDirectory = await createAdminUser({
        username: formValues.username.trim(),
        email: formValues.email.trim(),
        mobile: formValues.mobile.trim(),
        password: formValues.password,
      });
      setDirectory(nextDirectory);
      setFormValues(emptyForm);
      setCreateOpen(false);
      toast({ title: 'Ο λογαριασμός δημιουργήθηκε', description: 'Ο νέος διαχειριστής μπορεί πλέον να συνδεθεί.' });
    } catch (error) {
      if (error instanceof AdminUsersApiError) {
        setFieldErrors(error.fields);
        setFormError(error.message);
      } else {
        setFormError('Δεν ήταν δυνατή η δημιουργία του λογαριασμού.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteOpenChange = (open: boolean) => {
    if (isDeleting) return;
    if (!open) {
      setDeleteTarget(null);
      setDeleteError(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.canDelete) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      const username = deleteTarget.username;
      setDirectory(await deleteAdminUser(deleteTarget.id));
      setDeleteTarget(null);
      toast({ title: 'Ο λογαριασμός διαγράφηκε', description: `Ο ${username} δεν έχει πλέον πρόσβαση.` });
    } catch (error) {
      setDeleteError(error instanceof Error ? error.message : 'Δεν ήταν δυνατή η διαγραφή του λογαριασμού.');
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredAdmins = useMemo(() => {
    const query = searchTerm.trim().toLocaleLowerCase('el-GR');
    return directory.admins.filter((admin) => {
      const matchesStatus = statusFilter === 'all'
        || (statusFilter === 'active' && admin.active)
        || (statusFilter === 'inactive' && !admin.active);
      const matchesSearch = query === ''
        || admin.username.toLocaleLowerCase('el-GR').includes(query)
        || admin.email.toLocaleLowerCase('el-GR').includes(query)
        || admin.mobile.includes(query);
      return matchesStatus && matchesSearch;
    });
  }, [directory.admins, searchTerm, statusFilter]);

  const filterOptions: Array<{ key: StatusFilter; label: string; count: number }> = [
    { key: 'all', label: 'Όλοι', count: directory.summary.total },
    { key: 'active', label: 'Ενεργοί', count: directory.summary.active },
    { key: 'inactive', label: 'Ανενεργοί', count: directory.summary.inactive },
  ];
  const canRenderDirectory = loadError === null || directory.generatedAt !== null;

  return (
    <div className="animate-fade-in space-y-6 sm:space-y-8">
      <PageHeader
        title="Διαχειριστές"
        description="Προσωπικοί λογαριασμοί πρόσβασης, κατάσταση σύνδεσης και ασφαλής διαχείριση δικαιωμάτων."
        icon={<UserCog />}
        iconGradient="rose"
        eyebrow="Ασφάλεια πρόσβασης"
        actions={(
          <div className="grid w-full grid-cols-[44px_1fr] gap-2 sm:flex sm:w-auto">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-11 w-11"
              onClick={() => void loadAdmins(true)}
              disabled={isRefreshing || isFetching}
              aria-label="Ανανέωση λίστας διαχειριστών"
            >
              <RefreshCw className={cn('h-4 w-4', isRefreshing && 'animate-spin')} />
            </Button>
            <Button type="button" onClick={() => setCreateOpen(true)} className="h-11 bg-slate-950 px-4 text-white hover:bg-slate-800">
              <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
              Νέος διαχειριστής
            </Button>
          </div>
        )}
      />

      {isFetching ? (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {Array.from({ length: 4 }, (_, index) => <Skeleton key={index} className="h-32 rounded-2xl" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatCard title="Συνολικά" value={directory.summary.total} description="λογαριασμοί admin" icon={<UsersRound />} variant="default" size="compact" />
          <StatCard title="Ενεργοί" value={directory.summary.active} description="έχουν πρόσβαση" icon={<CheckCircle2 />} variant="success" size="compact" />
          <StatCard title="Χωρίς σύνδεση" value={directory.summary.neverLoggedIn} description="δεν έχουν μπει ακόμη" icon={<LogIn />} variant={directory.summary.neverLoggedIn > 0 ? 'warning' : 'info'} size="compact" />
          <StatCard title="Νέοι μήνα" value={directory.summary.createdThisMonth} description="δημιουργήθηκαν τώρα" icon={<CalendarPlus />} variant="info" size="compact" />
        </div>
      )}

      {!isFetching && directory.summary.active <= 1 && (
        <Alert className="rounded-2xl border-amber-200 bg-amber-50 text-amber-950">
          <AlertTriangle className="text-amber-600" />
          <AlertTitle>Μόνο ένας ενεργός διαχειριστής</AlertTitle>
          <AlertDescription className="leading-6 text-amber-800">
            Προσθέστε δεύτερο προσωπικό λογαριασμό ώστε να υπάρχει ασφαλής εναλλακτική πρόσβαση. Ο τελευταίος ενεργός admin προστατεύεται από διαγραφή.
          </AlertDescription>
        </Alert>
      )}

      {loadError && !isFetching && (
        <Alert variant="destructive" className="rounded-2xl bg-rose-50">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Η λίστα δεν φορτώθηκε</AlertTitle>
          <AlertDescription className="mt-2 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
            <span>{loadError}</span>
            <Button type="button" variant="outline" className="h-11 border-rose-200 bg-white" onClick={() => void loadAdmins()}>
              Δοκιμή ξανά
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {canRenderDirectory && (
        <Card className="rounded-2xl border-slate-200/80 shadow-[0_1px_2px_rgba(15,23,42,0.03)]">
          <CardContent className="space-y-4 p-4 sm:p-5">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
              <Input
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Αναζήτηση με username, email ή κινητό"
                aria-label="Αναζήτηση διαχειριστών"
                className="h-11 border-slate-200 bg-slate-50/70 pl-10 focus:bg-white"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1" role="group" aria-label="Φίλτρο κατάστασης">
              {filterOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setStatusFilter(option.key)}
                  aria-pressed={statusFilter === option.key}
                  className={cn(
                    'flex h-11 shrink-0 items-center gap-2 rounded-xl border px-3.5 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:ring-offset-2',
                    statusFilter === option.key
                      ? 'border-slate-950 bg-slate-950 text-white'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-950',
                  )}
                >
                  {option.label}
                  <span className={cn('rounded-full px-1.5 py-0.5 text-xs tabular-nums', statusFilter === option.key ? 'bg-white/15 text-white' : 'bg-slate-100 text-slate-600')}>
                    {option.count}
                  </span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {isFetching && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 3 }, (_, index) => <Skeleton key={index} className="h-[350px] rounded-2xl" />)}
        </div>
      )}

      {!isFetching && canRenderDirectory && filteredAdmins.length > 0 && (
        <section aria-label="Λογαριασμοί διαχειριστών" className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {filteredAdmins.map((admin) => (
            <AdminAccountCard key={admin.id} admin={admin} onDelete={(target) => { setDeleteError(null); setDeleteTarget(target); }} />
          ))}
        </section>
      )}

      {!isFetching && canRenderDirectory && filteredAdmins.length === 0 && (
        <EmptyState
          icon={<ShieldCheck />}
          title="Δεν βρέθηκαν λογαριασμοί"
          description={searchTerm || statusFilter !== 'all' ? 'Αλλάξτε την αναζήτηση ή τα φίλτρα.' : 'Δημιουργήστε τον πρώτο προσωπικό λογαριασμό admin.'}
          action={searchTerm || statusFilter !== 'all' ? (
            <Button type="button" variant="outline" className="h-11" onClick={() => { setSearchTerm(''); setStatusFilter('all'); }}>
              Καθαρισμός φίλτρων
            </Button>
          ) : (
            <Button type="button" className="h-11 bg-slate-950 text-white hover:bg-slate-800" onClick={() => setCreateOpen(true)}>
              <Plus className="mr-2 h-4 w-4" /> Νέος διαχειριστής
            </Button>
          )}
        />
      )}

      <AdminCreateDialog
        open={createOpen}
        values={formValues}
        fieldErrors={fieldErrors}
        generalError={formError}
        isSubmitting={isSubmitting}
        onOpenChange={handleCreateOpenChange}
        onChange={handleFormChange}
        onSubmit={() => void handleCreate()}
      />

      <AdminDeleteDialog
        admin={deleteTarget}
        isDeleting={isDeleting}
        error={deleteError}
        onOpenChange={handleDeleteOpenChange}
        onConfirm={() => void handleDelete()}
      />
    </div>
  );
}
